import { decode, JwtPayload } from 'jsonwebtoken'
import { isString } from 'lodash'
import { createContext, FC, ReactElement, useCallback, useContext, useEffect, useState } from 'react'
import config from '../../config'
import { setConfig } from '../../externals/api/config'
import {
  askCode as apiAskCode, resetPassword as apiResetPassword
} from '../../externals/api/forgotPassword'
import {
  login as apiLogin,
  logout as apiLogout,
  refreshToken, user as apiUser, User
} from '../../externals/api/login'
import {
  Register,
  register as registerApi, registerSocial as apiRegisterSocial, RegisterSocial,
  validateEmail as apiValidateEmail
} from '../../externals/api/register'
import { getSignedUploadUrl, uploadToS3 } from '../../externals/api/upload'
import { update as apiUpdate } from '../../externals/api/user'
import { request, validate } from '../../externals/api/verifyPhone'
import { useAlertHandler } from '../alertHandler'

type AuthStatus = 'pending' | 'logged' | 'not-logged';

type ForgotPassword = { email: string }

type ValidateEmail = {
  email: string
  phone: string
  name: string
  password: string
  zipCode: string
  occupation: string
  birthDate: Date
  code?: string
}

type UpdateUser = {
  avatar: File | string;
  phone: string
  name: string
  occupation: string
  birthDate: Date
}

interface IAuthContext {
  user?: User;
  accessToken?: string;
  status: AuthStatus;
  registerParams: ValidateEmail;
  login: (email: string, password: string, captcha?: string) => Promise<User>
  register: (register: Register, captcha?: string) => Promise<User>
  registerSocial: (register: RegisterSocial, captcha?: string) => Promise<User>
  loginSocial: (
    type: 'google' | 'facebook',
    accessToken: string,
    captcha?: string
  ) => Promise<User>,
  updateUser: (user: Partial<UpdateUser>) => Promise<void>,
  askCode: (email: string, captcha?: string) => Promise<void>,
  validateEmail: (validate: ValidateEmail, captcha?: string) => Promise<string>,
  forgotPassword: ForgotPassword,
  resetPassword: (email: string, password: string, token: string) => Promise<void>,
  validatePhone: (token: string) => Promise<void>,
  requestValidatePhone: () => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<IAuthContext>({} as IAuthContext)

export const useAuth = (): IAuthContext => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthContext')
  }

  return context
}

export const AuthProvider: FC = ({ children }): ReactElement => {
  const { showError } = useAlertHandler()
  const [status, setStatus] = useState<AuthStatus>('pending')
  const [registerParams, setRegisterParams] = useState({} as ValidateEmail)
  const [user, setUser] = useState<User>({} as User)
  const [accessToken, setAccessToken] = useState<string | undefined>()
  const [forgotPassword, setForgotPassword] = useState({} as ForgotPassword)

  useEffect(() => {
    if (accessToken) setConfig('ACCESS_TOKEN', accessToken)
  }, [accessToken])

  useEffect(() => {
    if (status === 'pending') {
      refreshToken().then(response => {
        setStatus('logged')
        setAccessToken(response.accessToken)
        setUser(response.user)
        apiUser().then(response => {
          setUser(response)
        })
      }).catch(() => {
        setStatus('not-logged')
      })
    }
  }, [status, setAccessToken, setUser])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let intervalTime = null as any
    const time = 5 * 1000

    if (status === 'logged' && accessToken) {
      const tokenDecoded = decode(accessToken) as JwtPayload

      intervalTime = setInterval(() => {
        console.log('INTERVAL CALLED')
        if (Date.now() >= ((tokenDecoded.exp || 0) * 1000) - 30000) {
          console.log('REFRESH TOKEN')
          refreshToken().then(response => {
            setAccessToken(response.accessToken)
          }).catch(() => {
            setStatus('not-logged')
          })
        }
      }, time)
    }

    return () => intervalTime && clearInterval(intervalTime)
  }, [status, accessToken])

  const login = useCallback(async (email: string, password: string, captcha?: string): Promise<User> => {
    try {
      const loggedUser = await apiLogin({ email, password, captcha })

      setUser(loggedUser.user)
      setStatus('logged')
      setAccessToken(loggedUser.accessToken)

      return loggedUser.user
    } catch (err) {
      showError(err)

      throw err
    }
  }, [setUser, setStatus, setAccessToken, showError])

  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiLogout()

      setUser({} as User)
      setStatus('not-logged')
      setAccessToken(undefined)
    } catch (err) {
      showError(err)

      throw err
    }
  }, [setUser, setStatus, setAccessToken, showError])

  const requestValidatePhone = useCallback(async (): Promise<void> => {
    try {
      await request()
    } catch (err) {
      showError(err)

      throw err
    }
  }, [setUser, setStatus, setAccessToken, showError])

  const validatePhone = useCallback(async (token: string): Promise<void> => {
    try {
      await validate(token)
      setUser(user => ({
        ...user,
        isVerified: true
      }))
    } catch (err) {
      showError(err)

      throw err
    }
  }, [setUser, setStatus, setAccessToken, showError])

  const register = useCallback(async (register: Register, captcha?: string): Promise<User> => {
    try {
      const loggedUser = await registerApi({ ...register, captcha })

      setUser(loggedUser.user)
      setStatus('logged')
      setAccessToken(loggedUser.accessToken)
      // setRegisterParams({} as ValidateEmail)

      return loggedUser.user
    } catch (err) {
      showError(err)

      throw err
    }
  }, [setUser, setStatus, setAccessToken, setRegisterParams, showError])

  const loginSocial = useCallback(async (
    type: 'google' | 'facebook',
    accessToken: string,
    captcha?: string
  ): Promise<User> => {
    try {
      const loggedUser = await apiLogin({ accessToken, type, captcha })

      setUser(loggedUser.user)
      setStatus('logged')
      setAccessToken(loggedUser.accessToken)

      return loggedUser.user
    } catch (err) {
      showError(err)

      throw err
    }
  }, [setUser, setStatus, setAccessToken])

  const registerSocial = useCallback(async (
    register: RegisterSocial,
    captcha?: string
  ): Promise<User> => {
    try {
      const loggedUser = await apiRegisterSocial({ ...register, captcha })

      setUser(loggedUser.user)
      setStatus('logged')
      setAccessToken(loggedUser.accessToken)

      return loggedUser.user
    } catch (err) {
      showError(err)

      throw err
    }
  }, [setUser, setStatus, setAccessToken])

  const updateUser = useCallback(async (userUpdate: Partial<UpdateUser>): Promise<void> => {
    try {
      let avatarString: string | undefined

      if (userUpdate.avatar && isString(userUpdate.avatar) === false) {
        const file = userUpdate.avatar as File

        const { headers, url } = await getSignedUploadUrl({
          genDiffFilename: true,
          filename: file.name,
          contentType: file.type,
          folder: `/avatars/${user?._id}`,
          environment: 'public'
        })

        const newHeaders = {
          ...headers,
          'Content-Type': file.type
        }

        await uploadToS3(url, file, newHeaders)

        avatarString = config.CDN_URL + '/' + url.split('?')[0].split('public/')[1]
      }

      const userUpdated = await apiUpdate({
        _id: user?._id || '',
        ...userUpdate,
        avatar: avatarString
      })

      setUser(user => ({
        ...user,
        ...userUpdate,
        avatar: avatarString || userUpdated.avatar
      }))
    } catch (err) {
      showError(err)

      throw err
    }
  }, [user, setUser, setStatus, setAccessToken])

  const validateEmail = useCallback(async (register: ValidateEmail, captcha?: string): Promise<string> => {
    try {
      const validateEmail = await apiValidateEmail({ email: register.email, code: register.code, captcha })

      setRegisterParams(register)

      return validateEmail.validationChecker
    } catch (err) {
      showError(err)

      throw err
    }
  }, [setUser, setStatus, setAccessToken])

  const askCode = useCallback(async (email: string, captcha?: string): Promise<void> => {
    try {
      await apiAskCode({ email, captcha })
      setForgotPassword({ email })
    } catch (err) {
      showError(err)

      throw err
    }
  }, [showError])

  const resetPassword = useCallback(async (email: string, password: string, token: string): Promise<void> => {
    try {
      await apiResetPassword({ email, password, token })
    } catch (err) {
      showError(err)

      throw err
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        status,
        user,
        accessToken,
        forgotPassword,
        registerParams,
        updateUser,
        login,
        loginSocial,
        askCode,
        resetPassword,
        validateEmail,
        register,
        validatePhone,
        requestValidatePhone,
        registerSocial,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
